'use client'

import { useState } from 'react'
import { useRouter, useParams } from 'next/navigation'
import Link from 'next/link'
import Image from 'next/image'
import { useLanguage } from '../lib/LanguageContext'
import { createClient } from '@/utils/supabase/client'
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Checkbox } from "@/components/ui/checkbox"
import { Loader2, Eye, EyeOff, Check } from 'lucide-react'
import { GoogleLogo } from "@/components/ui/google-logo"
import { ModeToggle } from '@/components/mode-toggle'
import { LanguageSwitcher } from '@/components/dashboard/LanguageSwitcher'
import { getAuthUrl, getMarketingUrl } from '@/lib/auth-urls'
import { StripedPattern } from '@/components/ui/striped-pattern'

export function SignUpPage() {
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [isGoogleLoading, setIsGoogleLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)

  const router = useRouter()
  const params = useParams()
  const locale = (params.locale as string) || 'ar'
  const { dir } = useLanguage()

  // Simplified Translations
  const t = {
    title: locale === 'ar' ? 'إنشاء حساب جديد' : 'Create an account',
    subtitle: locale === 'ar' ? 'انضم إلى آلاف التجار الناجحين' : 'Join thousands of successful merchants',
    fullName: locale === 'ar' ? 'الاسم الكامل' : 'Full Name',
    email: locale === 'ar' ? 'البريد الإلكتروني' : 'Email',
    phone: locale === 'ar' ? 'رقم الهاتف' : 'Phone',
    password: locale === 'ar' ? 'كلمة المرور' : 'Password',
    confirmPassword: locale === 'ar' ? 'تأكيد كلمة المرور' : 'Confirm Password',
    agree: locale === 'ar' ? 'أوافق على' : 'I agree to the',
    terms: locale === 'ar' ? 'شروط الخدمة' : 'Terms of Service',
    signup: locale === 'ar' ? 'إنشاء حساب' : 'Sign up',
    google: locale === 'ar' ? 'التسجيل مع Google' : 'Sign up with Google',
    haveAccount: locale === 'ar' ? 'لديك حساب بالفعل؟' : 'Already have an account?',
    login: locale === 'ar' ? 'تسجيل الدخول' : 'Sign in',
    or: locale === 'ar' ? 'أو' : 'OR',
    signingUp: locale === 'ar' ? 'جاري الإنشاء...' : 'Creating account...',
    passMismatch: locale === 'ar' ? 'كلمات المرور غير متطابقة' : 'Passwords do not match',
    passLength: locale === 'ar' ? 'كلمة المرور قصيرة جداً' : 'Password too short',
    success: locale === 'ar' ? 'تم إنشاء الحساب بنجاح!' : 'Account created successfully!',
    emailCheck: locale === 'ar' ? 'تحقق من بريدك الإلكتروني' : 'Check your email',
    features: [
      locale === 'ar' ? 'إعداد سريع في دقائق' : 'Setup in minutes',
      locale === 'ar' ? 'بدون رسوم خفية' : 'No hidden fees',
      locale === 'ar' ? 'دعم محلي 24/7' : '24/7 Local Support',
    ]
  }

  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    agreeToTerms: false
  })

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }))
    if (error) setError(null)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setSuccess(null)

    if (formData.password !== formData.confirmPassword) {
      setError(t.passMismatch)
      return
    }
    if (formData.password.length < 6) {
      setError(t.passLength)
      return
    }

    setIsLoading(true)

    try {
      const res = await fetch('/api/auth/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          fullName: formData.fullName,
          email: formData.email,
          phone: formData.phone,
          password: formData.password,
          agreeToTerms: formData.agreeToTerms,
        }),
      })

      const data = await res.json()

      if (!res.ok) {
        setError(data.error || 'Signup failed')
        return
      }

      if (data.requiresEmailConfirmation) {
        setSuccess(t.emailCheck)
        setTimeout(() => {
          router.push(`/${locale}/verify-email?email=${encodeURIComponent(formData.email)}`)
        }, 2000)
      } else {
        setSuccess(t.success)
        setTimeout(() => {
          router.push(`/${locale}/dashboard`)
        }, 1000)
      }

    } catch (err) {
      console.error(err)
      setError('An unexpected error occurred')
    } finally {
      setIsLoading(false)
    }
  }

  const handleGoogleSignup = async () => {
    setIsGoogleLoading(true)
    setError(null)
    const supabase = createClient()
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${window.location.origin}/auth/callback?redirect=/${locale}/dashboard`,
        queryParams: { access_type: 'offline' },
      },
    })
    if (error) {
      setError(error.message)
      setIsGoogleLoading(false)
    }
  }

  return (
    <div className="h-screen flex items-center justify-center bg-background relative overflow-hidden" dir={dir}>
      {/* Global Background Pattern - Lowest Z-Index */}
      <StripedPattern
        className="text-foreground/[0.03] dark:text-foreground/[0.02] z-0"
      />

      {/* Controls - Fixed to top start corner */}
      <div className="absolute top-4 left-4 rtl:left-auto rtl:right-4 z-50 flex items-center gap-2">
        <LanguageSwitcher locale={locale} />
        <ModeToggle />
      </div>

      <div className="w-full h-full grid lg:grid-cols-2 p-4 relative z-10">
        {/* Form Side */}
        <div className="flex h-full flex-col items-center justify-center">
          <div className="max-w-xs m-auto w-full flex flex-col items-center">
            <Link href={getMarketingUrl(locale)} className="mb-6 flex flex-col items-center gap-2 font-bold hover:opacity-90 transition-opacity">
              {/* Light Mode Logo (Black) */}
              <Image
                src="/logo-light.png"
                alt="Ecommaps Logo"
                width={40}
                height={40}
                className="size-10 object-contain block dark:hidden"
                unoptimized
              />
              {/* Dark Mode Logo (White) */}
              <Image
                src="/logo-dark.png"
                alt="Ecommaps Logo"
                width={40}
                height={40}
                className="size-10 object-contain hidden dark:block"
                unoptimized
              />
            </Link>

            <h1 className="text-xl font-semibold tracking-tight">{t.title}</h1>
            <p className="mt-2 text-center text-sm text-balance text-muted-foreground">{t.subtitle}</p>

            <div className="w-full mt-8 grid gap-6">
              {error && (
                <div className="bg-destructive/15 text-destructive text-sm p-3 rounded-md text-center">
                  {error}
                </div>
              )}
              {success && (
                <div className="bg-green-500/15 text-green-600 text-sm p-3 rounded-md text-center">
                  {success}
                </div>
              )}

              <Button
                variant="outline"
                type="button"
                className="w-full gap-3 bg-background hover:bg-muted/50 dark:ring-input border-input/50 dark:border-input border-b-2 shadow-sm shadow-zinc-950/15 ring-1 ring-zinc-300 rounded-md"
                disabled={isLoading || isGoogleLoading}
                onClick={handleGoogleSignup}
              >
                {isGoogleLoading ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <GoogleLogo />
                )}
                {t.google}
              </Button>

              <div className="relative text-center text-sm after:absolute after:inset-0 after:top-1/2 after:z-0 after:flex after:items-center after:border-t after:border-border">
                <span className="relative z-10 bg-background px-2 text-muted-foreground">
                  {t.or}
                </span>
              </div>

              <form onSubmit={handleSubmit} className="w-full space-y-4">
                <div className="grid gap-2">
                  <Label htmlFor="fullName">{t.fullName}</Label>
                  <Input
                    id="fullName"
                    name="fullName"
                    value={formData.fullName}
                    onChange={handleChange}
                    disabled={isLoading || isGoogleLoading}
                    required
                    className="bg-background/80 backdrop-blur-sm"
                  />
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="email">{t.email}</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    disabled={isLoading || isGoogleLoading}
                    required
                    className="bg-background/80 backdrop-blur-sm"
                  />
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="phone">{t.phone}</Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    disabled={isLoading || isGoogleLoading}
                    dir="ltr"
                    className="bg-background/80 backdrop-blur-sm"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="grid gap-2">
                    <Label htmlFor="password">{t.password}</Label>
                    <div className="relative">
                      <Input
                        id="password"
                        name="password"
                        type={showPassword ? "text" : "password"}
                        value={formData.password}
                        onChange={handleChange}
                        disabled={isLoading || isGoogleLoading}
                        required
                        className="bg-background/80 backdrop-blur-sm"
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent ltr:right-0 rtl:left-0 rtl:right-auto"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? <EyeOff className="h-4 w-4 text-muted-foreground" /> : <Eye className="h-4 w-4 text-muted-foreground" />}
                      </Button>
                    </div>
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="confirmPassword">{t.confirmPassword}</Label>
                    <div className="relative">
                      <Input
                        id="confirmPassword"
                        name="confirmPassword"
                        type={showConfirmPassword ? "text" : "password"}
                        value={formData.confirmPassword}
                        onChange={handleChange}
                        disabled={isLoading || isGoogleLoading}
                        required
                        className="bg-background/80 backdrop-blur-sm"
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent ltr:right-0 rtl:left-0 rtl:right-auto"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      >
                        {showConfirmPassword ? <EyeOff className="h-4 w-4 text-muted-foreground" /> : <Eye className="h-4 w-4 text-muted-foreground" />}
                      </Button>
                    </div>
                  </div>
                </div>

                <div className="flex items-center space-x-2 py-2">
                  <Checkbox
                    id="agreeToTerms"
                    name="agreeToTerms"
                    checked={formData.agreeToTerms}
                    onCheckedChange={(checked) => setFormData(prev => ({ ...prev, agreeToTerms: checked === true }))}
                    required
                  />
                  <label
                    htmlFor="agreeToTerms"
                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 px-2"
                  >
                    {t.agree} <Link href={`${getMarketingUrl(locale)}/legal/terms`} className="text-primary hover:underline" target="_blank">{t.terms}</Link>
                  </label>
                </div>

                <Button
                  type="submit"
                  className="w-full mt-4 from-primary to-primary/85 text-primary-foreground dark:inset-shadow-2xs dark:inset-shadow-white/10 bg-linear-to-t border border-b-2 border-zinc-950/40 shadow-md shadow-zinc-950/20 ring-1 ring-inset ring-white/25 transition-[filter] duration-200 hover:brightness-110 active:brightness-90 dark:border-x-0 dark:border-t-0 dark:border-zinc-950/50 dark:ring-white/5 rounded-md"
                  disabled={isLoading || isGoogleLoading}
                >
                  {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  {isLoading ? t.signingUp : t.signup}
                </Button>
              </form>

              <div className="mt-5 space-y-5 text-center">
                <p className="text-sm">
                  {t.haveAccount}{" "}
                  <Link href={getAuthUrl(locale, '/login')} className="ml-1 underline text-muted-foreground hover:text-primary transition-colors">
                    {t.login}
                  </Link>
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Image Side */}
        <div className="hidden bg-muted lg:block rounded-lg border relative overflow-hidden bg-slate-900 shadow-sm">
          <div className="absolute inset-0 bg-[url('https://ecodata.ecommaps.com/storage/v1/object/public/store-assets/signup-background-final.png')] bg-cover bg-center"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/40 to-transparent"></div>
          <div className="relative z-20 flex h-full flex-col justify-between p-12 text-white pointer-events-none">
            <div className="flex items-center gap-2 text-2xl font-bold">
              <Image
                src="/logo-dark.png"
                alt="Ecommaps Logo"
                width={32}
                height={32}
                className="w-8 h-8 object-contain"
                unoptimized
              />
              Ecommaps
            </div>
            <div className="space-y-6">
              <div className="space-y-4">
                {t.features.map((feature, i) => (
                  <div key={i} className="flex items-center gap-3 text-lg">
                    <div className="h-8 w-8 rounded-full bg-primary/20 flex items-center justify-center">
                      <Check className="h-4 w-4 text-primary" />
                    </div>
                    {feature}
                  </div>
                ))}
              </div>
              <blockquote className="space-y-2 pt-6 border-t border-white/10">
                <p className="text-lg font-medium">
                  &ldquo;Launching my business on Ecommaps was the best decision I ever made. The support is incredible.&rdquo;
                </p>
                <footer className="text-sm text-slate-300">Mohammed Ali, Owner of DzStore</footer>
              </blockquote>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
